/**
 * M-034: Policy Configuration UI
 *
 * Admin/senior officer screen for managing depositary policy profile.
 * CRUD on evidence requirements, validity periods, custom fields,
 * document types, and auto-confirm settings via M-008 Policy Config API.
 */

import { useState, useEffect, useCallback } from 'react'
import { useAuth } from '../contexts/useAuth'
import { apiFetch, ApiError } from '../lib/api'
import {
  FileText,
  ToggleLeft,
  ToggleRight,
  Plus,
  Trash2,
  Save,
  AlertTriangle,
  RefreshCw,
  X,
} from 'lucide-react'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface BaselineWarning {
  field: string
  baseline_value: unknown
  new_value: unknown
  message: string
  regulatory_reference: string
}

interface EvidenceRequirement {
  id: number
  name: string
  target_type: string
  mandatory: boolean
  max_age_months: number | null
  satisfying_document_types: string[]
  filters: Record<string, unknown>
  validity_criteria: Record<string, unknown> | null
}

interface ValidityPeriod {
  doc_type: string
  max_age_months: number | null
}

interface CustomField {
  name: string
  scoping: Record<string, unknown>
}

interface DocumentType {
  doc_type: string
  nature: string
}

interface AutoConfirm {
  enabled: boolean
  threshold: number
}

type TabKey = 'evidence' | 'staleness' | 'custom-fields' | 'document-types' | 'auto-confirm'

/* Policy v1 tabs RETIRED 2026-07-29. `POLICY_V2_CUTOVER` defaults true and no
 * live code reads evidence_requirements, the doc-type staleness map, custom
 * fields or the auto-confirm threshold any more. Those four tabs saved,
 * returned 200 and fired a recompute event while changing nothing the engine
 * demands of any object — a control that lies about its own effect.
 *
 * Auto-Confirm was the worst of them: a toggle and a confidence threshold
 * under seven precise-sounding conditions, for a rule engine that does not
 * exist in this codebase. Its believed effect was LESS human oversight, which
 * is the one thing a depositary must never be wrong about.
 *
 * Document Types stays: `policy_data["document_nature_map"]` is still read.
 * The live obligation catalogue is at /v2/admin/policy (v2/PolicyPage.tsx).
 */
const TABS: { key: TabKey; label: string; icon: React.ReactNode }[] = [
  { key: 'document-types', label: 'Document Types', icon: <FileText size={16} /> },
]

const DEPOSITARY_ID = '00000000-0000-0000-0000-000000000001'
const NATURE_OPTIONS = ['constitutive', 'evidential', 'hybrid', 'informational']
const SCOPE_OPTIONS = ['per_fund', 'per_entity_nature', 'per_document_type', 'per_asset_type']
const OBJECT_TYPES = ['entity', 'instrument', 'position'] as const

// ---------------------------------------------------------------------------
// Inline warning badge
// ---------------------------------------------------------------------------

function WarningBadge({ warnings }: { warnings: BaselineWarning[] }) {
  if (warnings.length === 0) return null
  return (
    <div className="mt-1">
      {warnings.map((w, i) => (
        <div
          key={i}
          className="flex items-start gap-2 text-xs px-2 py-1 rounded"
          style={{ backgroundColor: 'rgba(169,132,16,0.1)', color: 'var(--brand-gold)' }}
        >
          <AlertTriangle size={14} className="shrink-0 mt-0.5" />
          <span>{w.message}</span>
        </div>
      ))}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Evidence Requirements Section
// ---------------------------------------------------------------------------

function EvidenceRequirementsSection({ canWrite }: { canWrite: boolean }) {
  const [requirements, setRequirements] = useState<EvidenceRequirement[]>([])
  const [warnings, setWarnings] = useState<BaselineWarning[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [editValue, setEditValue] = useState('')
  const [rowWarnings, setRowWarnings] = useState<Record<number, BaselineWarning[]>>({})
  const [showAddForm, setShowAddForm] = useState(false)
  const [newReq, setNewReq] = useState({
    name: '',
    target_type: 'entity',
    mandatory: true,
    max_age_months: '',
    satisfying_document_types: '',
  })

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await apiFetch<{
        requirements: EvidenceRequirement[]
        warnings: BaselineWarning[]
      }>(`/api/policy/${DEPOSITARY_ID}/evidence-requirements`)
      setRequirements(data.requirements)
      setWarnings(data.warnings)
    } catch {
      setError('Failed to load evidence requirements')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const handleSaveAge = async (reqId: number) => {
    const val = editValue.trim()
    const parsed = val === '' ? null : parseInt(val, 10)
    if (parsed !== null && (isNaN(parsed) || parsed < 1)) {
      setRowWarnings((prev) => ({
        ...prev,
        [reqId]: [
          {
            field: 'max_age_months',
            baseline_value: null,
            new_value: parsed,
            message: 'Validity period must be a positive integer or empty (no expiry)',
            regulatory_reference: '',
          },
        ],
      }))
      return
    }
    try {
      const resp = await apiFetch<{
        requirement: EvidenceRequirement
        warnings: BaselineWarning[]
      }>(`/api/policy/${DEPOSITARY_ID}/evidence-requirements/${reqId}`, {
        method: 'PATCH',
        body: JSON.stringify({ max_age_months: parsed }),
      })
      setRequirements((prev) => prev.map((r) => (r.id === reqId ? resp.requirement : r)))
      setRowWarnings((prev) => ({ ...prev, [reqId]: resp.warnings }))
      setEditingId(null)
    } catch (e) {
      const msg =
        e instanceof ApiError
          ? String((e.body as Record<string, string>)?.detail ?? 'Save failed')
          : 'Save failed'
      setRowWarnings((prev) => ({
        ...prev,
        [reqId]: [
          {
            field: 'max_age_months',
            baseline_value: null,
            new_value: null,
            message: msg,
            regulatory_reference: '',
          },
        ],
      }))
    }
  }

  const handleDelete = async (reqId: number) => {
    try {
      await apiFetch(`/api/policy/${DEPOSITARY_ID}/evidence-requirements/${reqId}`, {
        method: 'DELETE',
      })
      setRequirements((prev) => prev.filter((r) => r.id !== reqId))
    } catch {
      // ignore
    }
  }

  const handleAdd = async () => {
    const maxAge = newReq.max_age_months.trim() === '' ? null : parseInt(newReq.max_age_months, 10)
    if (maxAge !== null && (isNaN(maxAge) || maxAge < 1)) return
    if (!newReq.name.trim()) return
    const docTypes = newReq.satisfying_document_types
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)
    if (docTypes.length === 0) return

    try {
      const resp = await apiFetch<{
        requirement: EvidenceRequirement
        warnings: BaselineWarning[]
      }>(`/api/policy/${DEPOSITARY_ID}/evidence-requirements`, {
        method: 'POST',
        body: JSON.stringify({
          name: newReq.name.trim(),
          target_type: newReq.target_type,
          mandatory: newReq.mandatory,
          max_age_months: maxAge,
          satisfying_document_types: docTypes,
        }),
      })
      setRequirements((prev) => [...prev, resp.requirement])
      setShowAddForm(false)
      setNewReq({
        name: '',
        target_type: 'entity',
        mandatory: true,
        max_age_months: '',
        satisfying_document_types: '',
      })
    } catch {
      // ignore
    }
  }

  // Group by target_type
  const grouped: Record<string, EvidenceRequirement[]> = {}
  for (const r of requirements) {
    ;(grouped[r.target_type] ??= []).push(r)
  }

  if (loading) return <SectionSpinner />
  if (error) return <SectionError message={error} onRetry={load} />

  return (
    <div className="space-y-4">
      <WarningBadge warnings={warnings} />

      {Object.entries(grouped).map(([targetType, reqs]) => (
        <div key={targetType}>
          <h4 className="text-sm font-semibold text-slate-700 mb-2 capitalize">
            {targetType.replace(/_/g, ' ')}
          </h4>
          <div className="border border-slate-200 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-xs">
                <tr>
                  <th className="text-left px-3 py-2">Name</th>
                  <th className="text-left px-3 py-2">Document types</th>
                  <th className="text-center px-3 py-2">Mandatory</th>
                  <th className="text-center px-3 py-2">Max age (months)</th>
                  <th className="text-right px-3 py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {reqs.map((r) => (
                  <tr key={r.id} className="border-t border-slate-100">
                    <td
                      className="px-3 py-2 font-medium text-slate-800"
                      style={{ fontFamily: 'var(--font-mono)' }}
                    >
                      {r.name}
                    </td>
                    <td className="px-3 py-2 text-slate-600">
                      {r.satisfying_document_types.join(', ')}
                    </td>
                    <td className="px-3 py-2 text-center">{r.mandatory ? 'Yes' : 'No'}</td>
                    <td className="px-3 py-2 text-center">
                      {canWrite && editingId === r.id ? (
                        <div className="flex items-center justify-center gap-1">
                          <input
                            type="text"
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            className="w-16 px-1 py-0.5 border border-slate-300 rounded text-center text-sm"
                            placeholder="null"
                          />
                          <button
                            onClick={() => handleSaveAge(r.id)}
                            className="text-nolos-primary hover:opacity-80"
                          >
                            <Save size={14} />
                          </button>
                          <button
                            onClick={() => setEditingId(null)}
                            className="text-slate-400 hover:text-slate-600"
                          >
                            <X size={14} />
                          </button>
                        </div>
                      ) : canWrite ? (
                        <button
                          onClick={() => {
                            setEditingId(r.id)
                            setEditValue(r.max_age_months?.toString() ?? '')
                            setRowWarnings((prev) => ({ ...prev, [r.id]: [] }))
                          }}
                          className="text-slate-700 hover:text-nolos-primary cursor-pointer"
                        >
                          {r.max_age_months ?? 'no expiry'}
                        </button>
                      ) : (
                        <span className="text-slate-700">{r.max_age_months ?? 'no expiry'}</span>
                      )}
                      <WarningBadge warnings={rowWarnings[r.id] ?? []} />
                    </td>
                    <td className="px-3 py-2 text-right">
                      {canWrite && (
                        <button
                          onClick={() => handleDelete(r.id)}
                          className="text-slate-400 hover:text-red-500"
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}

      {canWrite &&
        (showAddForm ? (
          <div className="border border-slate-200 rounded-lg p-4 space-y-3 bg-slate-50">
            <h4 className="text-sm font-semibold text-slate-700">New evidence requirement</h4>
            <div className="grid grid-cols-2 gap-3">
              <input
                placeholder="Name"
                value={newReq.name}
                onChange={(e) => setNewReq((p) => ({ ...p, name: e.target.value }))}
                className="input-field"
              />
              <select
                value={newReq.target_type}
                onChange={(e) => setNewReq((p) => ({ ...p, target_type: e.target.value }))}
                className="input-field"
              >
                {['entity', 'position', 'trade', 'instrument'].map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
              <input
                placeholder="Max age months (empty = no expiry)"
                value={newReq.max_age_months}
                onChange={(e) => setNewReq((p) => ({ ...p, max_age_months: e.target.value }))}
                className="input-field"
              />
              <input
                placeholder="Doc types (comma-separated)"
                value={newReq.satisfying_document_types}
                onChange={(e) =>
                  setNewReq((p) => ({ ...p, satisfying_document_types: e.target.value }))
                }
                className="input-field"
              />
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-600">
              <input
                type="checkbox"
                checked={newReq.mandatory}
                onChange={(e) => setNewReq((p) => ({ ...p, mandatory: e.target.checked }))}
              />
              Mandatory
            </label>
            <div className="flex gap-2">
              <button
                onClick={handleAdd}
                disabled={!newReq.name.trim()}
                className="btn-primary text-sm"
              >
                Add
              </button>
              <button onClick={() => setShowAddForm(false)} className="btn-secondary text-sm">
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <button
            onClick={() => setShowAddForm(true)}
            className="btn-secondary text-sm flex items-center gap-1"
          >
            <Plus size={14} /> Add requirement
          </button>
        ))}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Staleness Periods Section
// ---------------------------------------------------------------------------

function StalenessPeriodsSection({ canWrite }: { canWrite: boolean }) {
  const [periods, setPeriods] = useState<ValidityPeriod[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [editingType, setEditingType] = useState<string | null>(null)
  const [editValue, setEditValue] = useState('')
  const [rowWarnings, setRowWarnings] = useState<Record<string, BaselineWarning[]>>({})

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await apiFetch<{ periods: ValidityPeriod[] }>(
        `/api/policy/${DEPOSITARY_ID}/validity-periods`
      )
      setPeriods(data.periods)
    } catch {
      setError('Failed to load validity periods')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const handleSave = async (docType: string) => {
    const val = editValue.trim()
    const parsed = val === '' ? null : parseInt(val, 10)
    if (parsed !== null && (isNaN(parsed) || parsed < 1)) {
      setRowWarnings((prev) => ({
        ...prev,
        [docType]: [
          {
            field: 'max_age_months',
            baseline_value: null,
            new_value: parsed,
            message: 'Validity period must be a positive integer or empty (no expiry)',
            regulatory_reference: '',
          },
        ],
      }))
      return
    }
    try {
      const resp = await apiFetch<{ period: ValidityPeriod; warnings: BaselineWarning[] }>(
        `/api/policy/${DEPOSITARY_ID}/validity-periods/${docType}`,
        { method: 'PATCH', body: JSON.stringify({ max_age_months: parsed }) }
      )
      setPeriods((prev) => prev.map((p) => (p.doc_type === docType ? resp.period : p)))
      setRowWarnings((prev) => ({ ...prev, [docType]: resp.warnings }))
      setEditingType(null)
    } catch {
      setRowWarnings((prev) => ({
        ...prev,
        [docType]: [
          {
            field: 'max_age_months',
            baseline_value: null,
            new_value: null,
            message: 'Save failed',
            regulatory_reference: '',
          },
        ],
      }))
    }
  }

  if (loading) return <SectionSpinner />
  if (error) return <SectionError message={error} onRetry={load} />

  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-slate-500 text-xs">
          <tr>
            <th className="text-left px-3 py-2">Document type</th>
            <th className="text-center px-3 py-2">Max age (months)</th>
          </tr>
        </thead>
        <tbody>
          {periods.map((p) => (
            <tr key={p.doc_type} className="border-t border-slate-100">
              <td className="px-3 py-2 text-slate-800" style={{ fontFamily: 'var(--font-mono)' }}>
                {p.doc_type}
              </td>
              <td className="px-3 py-2 text-center">
                {canWrite && editingType === p.doc_type ? (
                  <div className="flex items-center justify-center gap-1">
                    <input
                      type="text"
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      className="w-16 px-1 py-0.5 border border-slate-300 rounded text-center text-sm"
                      placeholder="null"
                    />
                    <button
                      onClick={() => handleSave(p.doc_type)}
                      className="text-nolos-primary hover:opacity-80"
                    >
                      <Save size={14} />
                    </button>
                    <button
                      onClick={() => setEditingType(null)}
                      className="text-slate-400 hover:text-slate-600"
                    >
                      <X size={14} />
                    </button>
                  </div>
                ) : canWrite ? (
                  <button
                    onClick={() => {
                      setEditingType(p.doc_type)
                      setEditValue(p.max_age_months?.toString() ?? '')
                      setRowWarnings((prev) => ({ ...prev, [p.doc_type]: [] }))
                    }}
                    className="text-slate-700 hover:text-nolos-primary cursor-pointer"
                  >
                    {p.max_age_months ?? 'no expiry'}
                  </button>
                ) : (
                  <span className="text-slate-700">{p.max_age_months ?? 'no expiry'}</span>
                )}
                <WarningBadge warnings={rowWarnings[p.doc_type] ?? []} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Custom Fields Section
// ---------------------------------------------------------------------------

function CustomFieldsSection({ canWrite }: { canWrite: boolean }) {
  const [objectType, setObjectType] = useState<(typeof OBJECT_TYPES)[number]>('entity')
  const [fields, setFields] = useState<CustomField[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showAdd, setShowAdd] = useState(false)
  const [newField, setNewField] = useState({ name: '', scope: 'per_fund' })
  const [validationError, setValidationError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await apiFetch<{ fields: CustomField[] }>(
        `/api/policy/${DEPOSITARY_ID}/custom-fields/${objectType}`
      )
      setFields(data.fields)
    } catch {
      setError('Failed to load custom fields')
    } finally {
      setLoading(false)
    }
  }, [objectType])

  useEffect(() => {
    load()
  }, [load])

  const handleAdd = async () => {
    setValidationError(null)
    if (!newField.name.trim()) return
    if (fields.some((f) => f.name === newField.name.trim())) {
      setValidationError(
        `A custom field named '${newField.name.trim()}' already exists for ${objectType} fields`
      )
      return
    }
    try {
      const resp = await apiFetch<{ field: CustomField }>(
        `/api/policy/${DEPOSITARY_ID}/custom-fields/${objectType}`,
        {
          method: 'POST',
          body: JSON.stringify({ name: newField.name.trim(), scoping: { scope: newField.scope } }),
        }
      )
      setFields((prev) => [...prev, resp.field])
      setShowAdd(false)
      setNewField({ name: '', scope: 'per_fund' })
    } catch (e) {
      const msg =
        e instanceof ApiError
          ? String((e.body as Record<string, string>)?.detail ?? 'Add failed')
          : 'Add failed'
      setValidationError(msg)
    }
  }

  const handleDelete = async (name: string) => {
    try {
      await apiFetch(`/api/policy/${DEPOSITARY_ID}/custom-fields/${objectType}/${name}`, {
        method: 'DELETE',
      })
      setFields((prev) => prev.filter((f) => f.name !== name))
    } catch {
      // ignore
    }
  }

  if (loading) return <SectionSpinner />
  if (error) return <SectionError message={error} onRetry={load} />

  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        {OBJECT_TYPES.map((t) => (
          <button
            key={t}
            onClick={() => setObjectType(t)}
            className={`px-3 py-1 text-sm rounded-full transition-colors ${
              objectType === t
                ? 'bg-nolos-primary text-white'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            {t} fields
          </button>
        ))}
      </div>

      <div className="border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-xs">
            <tr>
              <th className="text-left px-3 py-2">Name</th>
              <th className="text-left px-3 py-2">Scope</th>
              <th className="text-right px-3 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {fields.length === 0 && (
              <tr>
                <td colSpan={3} className="px-3 py-4 text-center text-slate-400">
                  No custom fields defined
                </td>
              </tr>
            )}
            {fields.map((f) => (
              <tr key={f.name} className="border-t border-slate-100">
                <td className="px-3 py-2 text-slate-800" style={{ fontFamily: 'var(--font-mono)' }}>
                  {f.name}
                </td>
                <td className="px-3 py-2 text-slate-600">
                  {(f.scoping as Record<string, string>).scope ?? '—'}
                </td>
                <td className="px-3 py-2 text-right">
                  {canWrite && (
                    <button
                      onClick={() => handleDelete(f.name)}
                      className="text-slate-400 hover:text-red-500"
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {canWrite &&
        (showAdd ? (
          <div className="border border-slate-200 rounded-lg p-4 space-y-3 bg-slate-50">
            <h4 className="text-sm font-semibold text-slate-700">New custom field</h4>
            <div className="grid grid-cols-2 gap-3">
              <input
                placeholder="Field name"
                value={newField.name}
                onChange={(e) => {
                  setNewField((p) => ({ ...p, name: e.target.value }))
                  setValidationError(null)
                }}
                className="input-field"
              />
              <select
                value={newField.scope}
                onChange={(e) => setNewField((p) => ({ ...p, scope: e.target.value }))}
                className="input-field"
              >
                {SCOPE_OPTIONS.map((s) => (
                  <option key={s} value={s}>
                    {s.replace(/_/g, ' ')}
                  </option>
                ))}
              </select>
            </div>
            {validationError && (
              <div className="text-xs text-red-600 flex items-center gap-1">
                <AlertTriangle size={12} />
                {validationError}
              </div>
            )}
            <div className="flex gap-2">
              <button
                onClick={handleAdd}
                disabled={!newField.name.trim()}
                className="btn-primary text-sm"
              >
                Add
              </button>
              <button
                onClick={() => {
                  setShowAdd(false)
                  setValidationError(null)
                }}
                className="btn-secondary text-sm"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <button
            onClick={() => setShowAdd(true)}
            className="btn-secondary text-sm flex items-center gap-1"
          >
            <Plus size={14} /> Add field
          </button>
        ))}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Document Types Section
// ---------------------------------------------------------------------------

function DocumentTypesSection({ canWrite }: { canWrite: boolean }) {
  const [docTypes, setDocTypes] = useState<DocumentType[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [editingType, setEditingType] = useState<string | null>(null)
  const [editNature, setEditNature] = useState('')
  const [showAdd, setShowAdd] = useState(false)
  const [newDoc, setNewDoc] = useState({ doc_type: '', nature: 'evidential' })
  const [validationError, setValidationError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await apiFetch<{ document_types: DocumentType[] }>(
        `/api/policy/${DEPOSITARY_ID}/document-types`
      )
      setDocTypes(data.document_types)
    } catch {
      setError('Failed to load document types')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const handleSaveNature = async (docType: string) => {
    try {
      const resp = await apiFetch<{ document_type: DocumentType }>(
        `/api/policy/${DEPOSITARY_ID}/document-types/${docType}`,
        { method: 'PATCH', body: JSON.stringify({ nature: editNature }) }
      )
      setDocTypes((prev) => prev.map((d) => (d.doc_type === docType ? resp.document_type : d)))
      setEditingType(null)
    } catch {
      // ignore
    }
  }

  const handleAdd = async () => {
    setValidationError(null)
    if (!newDoc.doc_type.trim()) {
      setValidationError('Document type name is required')
      return
    }
    try {
      const resp = await apiFetch<{ document_type: DocumentType }>(
        `/api/policy/${DEPOSITARY_ID}/document-types`,
        {
          method: 'POST',
          body: JSON.stringify({ doc_type: newDoc.doc_type.trim(), nature: newDoc.nature }),
        }
      )
      setDocTypes((prev) => [...prev, resp.document_type])
      setShowAdd(false)
      setNewDoc({ doc_type: '', nature: 'evidential' })
    } catch {
      setValidationError('Failed to add document type')
    }
  }

  if (loading) return <SectionSpinner />
  if (error) return <SectionError message={error} onRetry={load} />

  return (
    <div className="space-y-4">
      <div className="border border-slate-200 rounded-lg overflow-hidden max-h-[32rem] overflow-y-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-xs sticky top-0">
            <tr>
              <th className="text-left px-3 py-2">Document type</th>
              <th className="text-left px-3 py-2">Nature</th>
            </tr>
          </thead>
          <tbody>
            {docTypes.map((d) => (
              <tr key={d.doc_type} className="border-t border-slate-100">
                <td className="px-3 py-2 text-slate-800" style={{ fontFamily: 'var(--font-mono)' }}>
                  {d.doc_type}
                </td>
                <td className="px-3 py-2">
                  {canWrite && editingType === d.doc_type ? (
                    <div className="flex items-center gap-1">
                      <select
                        value={editNature}
                        onChange={(e) => setEditNature(e.target.value)}
                        className="input-field text-sm py-0.5"
                      >
                        {NATURE_OPTIONS.map((n) => (
                          <option key={n} value={n}>
                            {n}
                          </option>
                        ))}
                      </select>
                      <button
                        onClick={() => handleSaveNature(d.doc_type)}
                        className="text-nolos-primary hover:opacity-80"
                      >
                        <Save size={14} />
                      </button>
                      <button
                        onClick={() => setEditingType(null)}
                        className="text-slate-400 hover:text-slate-600"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ) : canWrite ? (
                    <button
                      onClick={() => {
                        setEditingType(d.doc_type)
                        setEditNature(d.nature)
                      }}
                      className="text-slate-700 hover:text-nolos-primary cursor-pointer capitalize"
                    >
                      {d.nature}
                    </button>
                  ) : (
                    <span className="text-slate-700 capitalize">{d.nature}</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {canWrite &&
        (showAdd ? (
          <div className="border border-slate-200 rounded-lg p-4 space-y-3 bg-slate-50">
            <h4 className="text-sm font-semibold text-slate-700">New document type</h4>
            <div className="grid grid-cols-2 gap-3">
              <input
                placeholder="Document type name"
                value={newDoc.doc_type}
                onChange={(e) => {
                  setNewDoc((p) => ({ ...p, doc_type: e.target.value }))
                  setValidationError(null)
                }}
                className="input-field"
              />
              <select
                value={newDoc.nature}
                onChange={(e) => setNewDoc((p) => ({ ...p, nature: e.target.value }))}
                className="input-field"
              >
                {NATURE_OPTIONS.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </div>
            {validationError && (
              <div className="text-xs text-red-600 flex items-center gap-1">
                <AlertTriangle size={12} />
                {validationError}
              </div>
            )}
            <div className="flex gap-2">
              <button
                onClick={handleAdd}
                disabled={!newDoc.doc_type.trim()}
                className="btn-primary text-sm"
              >
                Add
              </button>
              <button
                onClick={() => {
                  setShowAdd(false)
                  setValidationError(null)
                }}
                className="btn-secondary text-sm"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <button
            onClick={() => setShowAdd(true)}
            className="btn-secondary text-sm flex items-center gap-1"
          >
            <Plus size={14} /> Add document type
          </button>
        ))}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Auto-Confirm Section
// ---------------------------------------------------------------------------

function AutoConfirmSection({ canWrite }: { canWrite: boolean }) {
  const [data, setData] = useState<AutoConfirm | null>(null)
  const [warnings, setWarnings] = useState<BaselineWarning[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [pendingThreshold, setPendingThreshold] = useState('')

  const AUTO_CONFIRM_CONDITIONS = [
    'Single-source, single-object proposal',
    'Confidence >= auto_confirm_threshold',
    'No conflict_flags',
    'Peer Reviewer status = agreement',
    'Not a deletion or merge',
    'Entity is not marked sensitive',
    'Fund is not in regulatory-escalation mode',
  ]

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const resp = await apiFetch<{ auto_confirm: AutoConfirm; warnings: BaselineWarning[] }>(
        `/api/policy/${DEPOSITARY_ID}/auto-confirm`
      )
      setData(resp.auto_confirm)
      setWarnings(resp.warnings)
      setPendingThreshold(resp.auto_confirm.threshold.toString())
    } catch {
      setError('Failed to load auto-confirm settings')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const toggle = async () => {
    if (!data) return
    try {
      const resp = await apiFetch<{ auto_confirm: AutoConfirm; warnings: BaselineWarning[] }>(
        `/api/policy/${DEPOSITARY_ID}/auto-confirm`,
        { method: 'PATCH', body: JSON.stringify({ enabled: !data.enabled }) }
      )
      setData(resp.auto_confirm)
      setWarnings(resp.warnings)
    } catch {
      // ignore
    }
  }

  const saveThreshold = async () => {
    const val = parseFloat(pendingThreshold)
    if (isNaN(val) || val < 0 || val > 1) return
    try {
      const resp = await apiFetch<{ auto_confirm: AutoConfirm; warnings: BaselineWarning[] }>(
        `/api/policy/${DEPOSITARY_ID}/auto-confirm`,
        { method: 'PATCH', body: JSON.stringify({ threshold: val }) }
      )
      setData(resp.auto_confirm)
      setWarnings(resp.warnings)
    } catch {
      // ignore
    }
  }

  if (loading) return <SectionSpinner />
  if (error) return <SectionError message={error} onRetry={load} />
  if (!data) return null

  return (
    <div className="space-y-6">
      {/* Toggle */}
      <div className="flex items-center justify-between">
        <div>
          <h4 className="text-sm font-semibold text-slate-700">Auto-confirm enabled</h4>
          <p className="text-xs text-slate-500 mt-0.5">
            When disabled, all proposals require human review
          </p>
        </div>
        {canWrite ? (
          <button
            onClick={toggle}
            className="text-nolos-primary hover:opacity-80 transition-opacity"
          >
            {data.enabled ? (
              <ToggleRight size={32} />
            ) : (
              <ToggleLeft size={32} className="text-slate-400" />
            )}
          </button>
        ) : (
          <span className="text-nolos-primary">
            {data.enabled ? (
              <ToggleRight size={32} />
            ) : (
              <ToggleLeft size={32} className="text-slate-400" />
            )}
          </span>
        )}
      </div>

      {/* Threshold */}
      <div>
        <h4 className="text-sm font-semibold text-slate-700 mb-1">Confidence threshold</h4>
        <p className="text-xs text-slate-500 mb-2">
          Proposals with confidence below this value require human review
        </p>
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={pendingThreshold}
            onChange={(e) => setPendingThreshold(e.target.value)}
            disabled={!canWrite}
            className="w-20 px-2 py-1 border border-slate-300 rounded text-sm text-center disabled:bg-slate-50 disabled:text-slate-500"
            style={{ fontFamily: 'var(--font-mono)' }}
          />
          {canWrite && (
            <button onClick={saveThreshold} className="btn-primary text-sm">
              Save
            </button>
          )}
        </div>
      </div>

      <WarningBadge warnings={warnings} />

      {/* Auto-confirm conditions */}
      <div>
        <h4 className="text-sm font-semibold text-slate-700 mb-2">
          Auto-confirm conditions (read-only)
        </h4>
        <ol className="list-decimal list-inside space-y-1">
          {AUTO_CONFIRM_CONDITIONS.map((c, i) => (
            <li key={i} className="text-xs text-slate-600">
              {c}
            </li>
          ))}
        </ol>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Shared components
// ---------------------------------------------------------------------------

function SectionSpinner() {
  return (
    <div className="flex items-center justify-center py-12">
      <div className="w-5 h-5 border-2 border-nolos-primary border-t-transparent rounded-full animate-spin" />
    </div>
  )
}

function SectionError({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="flex flex-col items-center py-8 gap-2 text-slate-400">
      <p className="text-sm text-red-500">{message}</p>
      <button
        onClick={onRetry}
        className="flex items-center gap-1 text-xs text-nolos-primary hover:underline"
      >
        <RefreshCw size={12} /> Retry
      </button>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Main
// ---------------------------------------------------------------------------

export default function PolicyConfigPage() {
  const { user } = useAuth()
  const [activeTab, setActiveTab] = useState<TabKey>('document-types')

  if (!user || (user.role !== 'admin' && user.role !== 'manager' && user.role !== 'ops_officer')) {
    return (
      <div
        className="bg-white p-6"
        style={{ borderRadius: 'var(--radius-card)', boxShadow: 'var(--shadow-card)' }}
      >
        <p className="text-sm text-red-600">Access denied</p>
      </div>
    )
  }

  const canWrite = user.role === 'admin' || user.role === 'manager'

  return (
    <div className="space-y-4">
      {/* Header */}
      <div
        className="bg-white p-6"
        style={{ borderRadius: 'var(--radius-card)', boxShadow: 'var(--shadow-card)' }}
      >
        <h2 className="text-lg font-semibold text-slate-900">Policy Configuration</h2>
        <p className="text-sm text-slate-500 mt-1">
          {canWrite
            ? 'Map document types to how they behave. The obligations the engine runs on are under Policy in the officer app.'
            : 'View evidence requirements, staleness periods, custom fields, document types, and auto-confirm settings. Read-only access — only managers and admins can edit.'}
        </p>
        {!canWrite && (
          <div
            className="mt-3 flex items-center gap-2 text-xs px-3 py-2 rounded"
            style={{ backgroundColor: 'rgba(169,132,16,0.1)', color: 'var(--brand-gold)' }}
            data-testid="policy-readonly-banner"
          >
            <AlertTriangle size={14} className="shrink-0" />
            <span>Read-only view. Policy changes require manager or admin role.</span>
          </div>
        )}
      </div>

      {/* Tabs + content */}
      <div
        className="bg-white"
        style={{ borderRadius: 'var(--radius-card)', boxShadow: 'var(--shadow-card)' }}
      >
        {/* Tab bar */}
        <div className="flex border-b border-slate-200 overflow-x-auto">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-1.5 px-4 py-3 text-sm whitespace-nowrap border-b-2 transition-colors ${
                activeTab === tab.key
                  ? 'border-nolos-primary text-nolos-primary font-medium'
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab content */}
        <div className="p-6">
          {activeTab === 'evidence' && <EvidenceRequirementsSection canWrite={canWrite} />}
          {activeTab === 'staleness' && <StalenessPeriodsSection canWrite={canWrite} />}
          {activeTab === 'custom-fields' && <CustomFieldsSection canWrite={canWrite} />}
          {activeTab === 'document-types' && <DocumentTypesSection canWrite={canWrite} />}
          {activeTab === 'auto-confirm' && <AutoConfirmSection canWrite={canWrite} />}
        </div>
      </div>
    </div>
  )
}
